import { useState, useEffect } from 'react';

import ArchiveIcon from '@mui/icons-material/Archive';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Stack, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import { ContentType, LandingPage, Template } from 'domains/content/types';
import Button from 'domains/core/components/Button';
import DropdownButton, { DropdownButtonOption } from 'domains/core/components/DropdownButton';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import StatusFilterDropdown from 'domains/core/components/StatusFilterDropdown';
import ContentIcon from 'domains/core/components/SvgIcons/ContentIcon';
import MutationKeys from 'hooks/mutations/keys';
import useDebounce from 'hooks/useDebounce';
import useMutationStatus from 'hooks/useMutationStatus';
import { URLPaths, MutationStatus, SortDirection } from 'models/enums';
import { Folder } from 'models/types';
import { SEARCH_DEBOUNCE_MS } from 'utils';

import ContentTable from '../ContentTable';
import { ContentStatus } from '../ContentTable/ContentStatusText';
import { Props as ContentTableProps } from '../ContentTable/ContentTable';

const contentStatusOptions: MultiSelectOption<ContentStatus>[] = [
    { value: ContentStatus.DRAFT, label: 'Draft' },
    { value: ContentStatus.LIVE, label: 'Live' },
    { value: ContentStatus.PUBLISHED, label: 'Published' },
];

export const contentTypeToLabel = (type: ContentType | null): string => {
    if (!type) return ContentType.ALL;
    if (type === ContentType.DIRECT_MAIL) return 'Direct Mail';
    if (type === ContentType.PAGE) return 'Landing Page';
    if (type === ContentType.SMS) return 'Text Message';
    if (type === ContentType.VOICE) return 'Voice Call';

    return type;
};

export type ContentPaginationProps = {
    page: number;
    totalPages: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onRowsPerPageChange: (rowsPerPage: number) => void;
};

export type Props = {
    contentQuery: ContentTableProps<Template | LandingPage | Folder>['contentQuery'];
    /** When set (paginated list), empty state uses this count instead of `data.length`. */
    contentTotalCount?: number;
    createContentDropdownOptions: DropdownButtonOption[];
    headerOtherDropdownProps?: {
        label: string;
        options: DropdownButtonOption[];
    };
    isNestedContent?: boolean;
    isArchivedView?: boolean;
    onToggleArchivedView?: () => void;
    searchTerm?: string;
    onSearchTermChange?: (value: string) => void;
    selectedStatuses?: ContentStatus[];
    onStatusFilterApply?: (params: { newStatuses?: ContentStatus[] }) => void;
    /** When false, hides Create in header and row manage actions (content.manage). Default true. */
    canManageContent?: boolean;
    contentPagination?: ContentPaginationProps;
    onContentSort?: (sortColumn: string, direction: SortDirection) => void;
    manualContentSort?: boolean;
};

const ContentPageLayout = ({
    contentQuery,
    contentTotalCount,
    createContentDropdownOptions,
    headerOtherDropdownProps,
    isNestedContent,
    isArchivedView = false,
    onToggleArchivedView,
    searchTerm,
    onSearchTermChange,
    selectedStatuses = [],
    onStatusFilterApply,
    canManageContent = true,
    contentPagination,
    onContentSort,
    manualContentSort = false,
}: Props) => {
    const { contentDraft: hasContentDraft, htmlTemplateImport: hasHtmlTemplateImport } = useFlags();
    const history = useHistory();

    const [searchInputValue, setSearchInputValue] = useState(searchTerm ?? '');
    const debouncedSearch = useDebounce(searchInputValue, SEARCH_DEBOUNCE_MS);

    // Sync local input when parent clears search externally
    useEffect(() => {
        if (!searchTerm && searchInputValue) setSearchInputValue('');
    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onSearchTermChange?.(debouncedSearch);
    }, [debouncedSearch, onSearchTermChange]);
    const createLandingPageStatus = useMutationStatus(MutationKeys.CREATE_LANDING_PAGE);
    const createTemplateStatus = useMutationStatus(MutationKeys.CREATE_TEMPLATE);
    const updateLandingPageStatus = useMutationStatus(MutationKeys.UPDATE_LANDING_PAGE);
    const updateTemplateStatus = useMutationStatus(MutationKeys.UPDATE_TEMPLATE);
    const createFolderStatus = useMutationStatus(MutationKeys.CREATE_CONTENT_FOLDER);
    const createVoiceContentStatus = useMutationStatus(MutationKeys.CREATE_VOICE_CONTENT);
    const updateVoiceContentStatus = useMutationStatus(MutationKeys.UPDATE_VOICE_CONTENT);
    const createSmsStatus = useMutationStatus(MutationKeys.CREATE_SMS);
    const updateSmsStatus = useMutationStatus(MutationKeys.UPDATE_SMS);
    const createWhatsAppStatus = useMutationStatus(MutationKeys.CREATE_WHATSAPP_TEMPLATE);
    const updateWhatsAppStatus = useMutationStatus(MutationKeys.UPDATE_WHATSAPP_TEMPLATE);

    const { data, isError, isLoading, isRefetching, isSuccess } = contentQuery;

    const isEmpty = contentTotalCount !== undefined ? isSuccess && contentTotalCount === 0 : isSuccess && !data?.length;
    const hasStatusFilter = selectedStatuses.length > 0;
    const shouldShowCreateButton =
        canManageContent && (!isEmpty || isNestedContent || searchTerm || hasStatusFilter) && !isArchivedView;

    const isHeaderActionsDisabled = isLoading || isRefetching;

    const CreateButton = (
        <DropdownButton
            aria-label="Choose content type to create"
            data-testid="content-create-button"
            label="Create"
            variant="outlined"
            options={createContentDropdownOptions}
            icon={<ContentIcon />}
            disabled={isHeaderActionsDisabled}
        />
    );

    const handleStatusFilterApply = (statuses: ContentStatus[]) => {
        onStatusFilterApply?.({ newStatuses: statuses });
    };

    const renderHeaderOthers = () => {
        if (!headerOtherDropdownProps && !onStatusFilterApply && !onToggleArchivedView && !onSearchTermChange) return;

        const isControlsDisabled = isLoading || isRefetching;

        return (
            <Stack direction="row" spacing={0} alignItems="center">
                {headerOtherDropdownProps && (
                    <DropdownButton
                        sx={{ textTransform: 'capitalize' }}
                        aria-label="Choose which type of content to view"
                        variant="text"
                        icon={<KeyboardArrowDownIcon />}
                        disabled={isControlsDisabled}
                        {...headerOtherDropdownProps}
                    />
                )}

                {hasContentDraft && onStatusFilterApply && (
                    <StatusFilterDropdown
                        options={contentStatusOptions}
                        items={data ?? []}
                        selectedValues={selectedStatuses}
                        onApply={handleStatusFilterApply}
                        label="Status"
                        testIdPrefix="status-filter"
                        menuId="status-filter-menu"
                        disabled={isControlsDisabled}
                    />
                )}

                {onToggleArchivedView && (
                    <Tooltip
                        key={`content-archive-tooltip-${isArchivedView}`}
                        title={isControlsDisabled ? '' : isArchivedView ? 'Original content' : 'Archive list'}
                    >
                        <span>
                            <Button
                                onClick={onToggleArchivedView}
                                disabled={isControlsDisabled}
                                data-testid="archive-toggle-button"
                                sx={{ ml: 0, mr: 0.5 }}
                            >
                                {isArchivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {onSearchTermChange && (
                    <SearchField
                        placeholder="Search content..."
                        value={searchInputValue}
                        onChange={setSearchInputValue}
                        disabled={isControlsDisabled}
                        dataTestId="content-search-input"
                    />
                )}
            </Stack>
        );
    };

    const renderPageBody = () => {
        if (isError) {
            return (
                <EmptyStateLayout
                    caption="Please try refreshing the page."
                    header="There was a problem loading the content."
                />
            );
        }

        // Show empty table if search term or status filter exists, even when empty
        const hasSearchTerm = searchInputValue && searchInputValue.trim().length > 0;
        const shouldShowEmptyTable = isEmpty && (isNestedContent || hasSearchTerm || hasStatusFilter);

        if (isEmpty && !isNestedContent && !hasSearchTerm && !hasStatusFilter) {
            return (
                <EmptyStateLayout
                    caption={
                        isArchivedView
                            ? "You don't have any archived content."
                            : 'You currently do not have any content saved.'
                    }
                    header={isArchivedView ? 'No archived content' : "let's design your content"}
                >
                    {!isArchivedView && canManageContent && CreateButton}
                </EmptyStateLayout>
            );
        }

        return (
            <ContentTable
                contentQuery={contentQuery}
                hasActiveFilters={hasStatusFilter}
                shouldShowEmptyTable={shouldShowEmptyTable}
                searchTerm={searchTerm}
                canManageContent={canManageContent}
                pagination={contentPagination}
                onSort={onContentSort}
                manualSortBy={manualContentSort}
            />
        );
    };

    return (
        <PageLayout
            header={isArchivedView ? 'archived content' : 'content'}
            headerIcon={<ContentIcon fontSize="inherit" />}
            headerAction={
                shouldShowCreateButton || (hasHtmlTemplateImport && canManageContent && !isArchivedView) ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {hasHtmlTemplateImport && canManageContent && !isArchivedView && (
                            <Button
                                data-testid="content-import-button"
                                endIcon={<FileDownloadIcon />}
                                variant="outlined"
                                onClick={() => history.push(URLPaths.CONTENT_IMPORT_TEMPLATE)}
                                disabled={isHeaderActionsDisabled}
                            >
                                Import
                            </Button>
                        )}
                        {shouldShowCreateButton && CreateButton}
                    </Stack>
                ) : undefined
            }
            headerOthers={renderHeaderOthers()}
        >
            {createTemplateStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Email successfully saved." />
            )}
            {updateTemplateStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Email successfully updated." />
            )}
            {createLandingPageStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Landing page successfully saved." />
            )}
            {updateLandingPageStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Landing page successfully updated." />
            )}
            {createFolderStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Folder created successfully." />
            )}
            {createVoiceContentStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Voice content created successfully." />
            )}
            {updateVoiceContentStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Voice content successfully updated." />
            )}
            {createSmsStatus === MutationStatus.SUCCESS && <SuccessSnackbar successMessage="SMS successfully saved." />}
            {updateSmsStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="SMS successfully updated." />
            )}
            {createWhatsAppStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="WhatsApp template successfully saved." />
            )}
            {updateWhatsAppStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="WhatsApp template successfully updated." />
            )}
            {renderPageBody()}
        </PageLayout>
    );
};

export default ContentPageLayout;
